import { SuppliesOrderStatus } from "@/constants/entity-status";

const normalize = (raw?: string) =>
  String(raw ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "");

/**
 * El backend no es consistente con el string de "en camino" para pedidos de
 * insumos (a veces llega "En transito", a veces "En camino"), así que se
 * normaliza y se compara por substring en vez de igualdad estricta contra
 * SuppliesOrderStatus.IN_TRANSIT.
 */
export const isSuppliesOrderInTransit = (estado?: string) => {
  const normalized = normalize(estado);
  return (
    normalized === normalize(SuppliesOrderStatus.IN_TRANSIT) ||
    normalized.includes("camino")
  );
};

/**
 * Un pedido queda bloqueado cuando ya no admite cambios: salió a la calle
 * (en tránsito), se entregó o se canceló. El resto de los estados
 * (Pendiente, Confirmado, No entregado, Rechazado) siguen siendo accionables.
 *
 * Es el predicado único que decide si un pedido se puede editar, cancelar o
 * reasignar, y si la solicitud muestra el botón "Editar pedidos": antes estaba
 * duplicado en cada tabla, cada página de edición y cada detalle, y las copias
 * se habían desincronizado entre sí.
 */
export const isSuppliesOrderLocked = (estado?: string) => {
  const normalized = normalize(estado);
  return (
    isSuppliesOrderInTransit(estado) ||
    normalized === normalize(SuppliesOrderStatus.DELIVERED) ||
    normalized === normalize(SuppliesOrderStatus.CANCELLED)
  );
};

export const isSuppliesOrderEditable = (estado?: string) =>
  !isSuppliesOrderLocked(estado);

/**
 * Un pedido "por mostrador" (esMostrador: true, sin chofer) admite marcarse
 * como entregado manualmente, salvo que ya esté en un estado final: el
 * backend devuelve 400 si se llama a /entregar sobre Entregado, Rechazado,
 * No entregado o Cancelado.
 */
export const canMarkSuppliesOrderAsDelivered = (
  estado?: string,
  esMostrador?: boolean | null,
) => {
  if (!esMostrador) return false;

  const normalized = normalize(estado);
  const finalStates = [
    SuppliesOrderStatus.DELIVERED,
    SuppliesOrderStatus.REJECTED,
    SuppliesOrderStatus.NOT_DELIVERED,
    SuppliesOrderStatus.CANCELLED,
  ];

  return !finalStates.some((s) => normalize(s) === normalized);
};
